<template>
  <div class="c-field-presets-tab">
    <div class="flex justify-between">
      <div class="flex flex-col gap-8">
        <p class="font-bold text-lg">Define sharing presets</p>
        <p class="text-neutral-text">
          Define reusable sets of fields that control what data is shared with
          partners.
          <br />
          Presets can be applied when configuring sharing settings for
          populations.
        </p>
      </div>
      <BittsButton
        text="Create preset"
        size="medium"
        class="flex self-start"
        @click="handleCreatePreset"
        :disabled="!ready"
      />
    </div>
    <BittsAlert
      color="info"
      message="Create a custom preset to tailor which fields you share with partners."
    />
    <BittsLoading :is-loading="!ready">
      <ListContainer
        :list="sortedPresets"
        :get-key="(preset) => preset.id"
        :has-divider="() => false"
        class="c-field-presets-tab__list"
      >
        <template #header>
          <div class="c-field-presets-tab__header">
            <div class="text-sm font-bold text-neutral-text"> Preset Name </div>
            <div class="text-sm font-bold text-neutral-text">Used by</div>
            <div class="text-sm font-bold text-neutral-text">Fields</div>
            <div></div>
          </div>
        </template>
        <template #default="{ item: preset, index }">
          <div
            class="c-field-presets-tab__item"
            :class="{
              'c-field-presets-tab__item--first': index === 0,
              'c-field-presets-tab__item--last':
                index + 1 === sortedPresets.length,
            }"
            @click="handleEditPreset(preset.id)"
          >
            <div class="flex items-center gap-12">
              <div class="font-bold">{{ preset.name }}</div>
              <template v-if="isTeamDefault(preset)">
                <BittsTag
                  v-if="hasTeamDefaultFlag"
                  text="Default"
                  color="info"
                />
                <BittsTooltip v-else placement="bottomLeft">
                  <BittsTag text="Recommended" color="info" />
                  <template #title>
                    Crossbeam provides a recommended preset based on best
                    practices for secure and effective data sharing.
                  </template>
                </BittsTooltip>
              </template>
            </div>
            <div class="text-neutral-text">
              <BittsTooltip
                v-if="hasPartnersUsingPreset(preset.id)"
                placement="bottomLeft"
              >
                <span>{{ getUsageCount(preset.id) }}</span>
                <template #title>
                  <div
                    v-for="partner in getPartnersForPreset(preset.id).slice(
                      0,
                      10,
                    )"
                    :key="partner.id"
                    class="flex items-center gap-4"
                  >
                    <BittsAvatar
                      :org="partner"
                      size="x-small"
                      :rounded="false"
                    />
                    <span>{{ partner.name }}</span>
                  </div>
                  <div
                    v-if="getPartnersForPreset(preset.id).length > 10"
                    class="text-neutral-text-weak"
                  >
                    +{{ getPartnersForPreset(preset.id).length - 10 }} more
                  </div>
                </template>
              </BittsTooltip>
              <span v-else>{{ getUsageCount(preset.id) }}</span>
            </div>
            <div class="flex items-center gap-12">
              <SharingSettingsVisibilitySelector
                placement="right"
                :read-only="true"
                display="fields"
                :data-share="{
                  source_field_ids: preset.source_fields.map(
                    (field) => field.source_field_id,
                  ),
                }"
              />
            </div>
            <div class="flex justify-end" @click.stop>
              <BittsPopover
                placement="bottomRight"
                :open="openMenuPresetId === preset.id"
                @update:open="(isOpen) => setMenuOpen(preset.id, isOpen)"
              >
                <template #trigger>
                  <BittsButton
                    type="neutral"
                    variant="ghost"
                    size="small"
                    :left-icon="['fak', 'ellipsis']"
                  />
                </template>
                <template #content>
                  <div class="c-field-presets-tab__actions-menu">
                    <button
                      type="button"
                      class="c-field-presets-tab__action-item"
                      @click="handleDuplicate(preset)"
                    >
                      <FontAwesomeIcon
                        :icon="['fak', 'copy']"
                        style="height: 16px; width: 16px; color: currentColor"
                        class="text-neutral-accent"
                      />
                      <span>Duplicate</span>
                    </button>
                    <button
                      v-if="canSetTeamDefault && !isTeamDefault(preset)"
                      type="button"
                      class="c-field-presets-tab__action-item"
                      @click="handleSetTeamDefault(preset)"
                    >
                      <FontAwesomeIcon
                        :icon="['far', 'star']"
                        style="height: 16px; width: 16px; color: currentColor"
                        class="text-neutral-accent"
                      />
                      <span>Set as default</span>
                    </button>
                    <component
                      v-if="canDelete(preset)"
                      :is="
                        getDeleteBlockedReason(preset) ? BittsTooltip : 'div'
                      "
                      placement="right"
                    >
                      <button
                        type="button"
                        class="c-field-presets-tab__action-item"
                        :class="{
                          'cursor-not-allowed':
                            !!getDeleteBlockedReason(preset),
                        }"
                        :disabled="!!getDeleteBlockedReason(preset)"
                        @click="handleDelete(preset)"
                      >
                        <FontAwesomeIcon
                          :icon="['fak', 'delete']"
                          style="height: 16px; width: 16px; color: currentColor"
                          class="text-neutral-accent"
                        />
                        <span
                          :class="{
                            'text-neutral-text-weak':
                              !!getDeleteBlockedReason(preset),
                          }"
                        >
                          Delete
                        </span>
                      </button>
                      <template v-if="getDeleteBlockedReason(preset)" #title>
                        {{ getDeleteBlockedReason(preset) }}
                      </template>
                    </component>
                  </div>
                </template>
              </BittsPopover>
            </div>
          </div>
        </template>
      </ListContainer>
    </BittsLoading>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsAvatar,
  BittsButton,
  BittsLoading,
  BittsPopover,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { pluralize } from 'humanize-plus';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';

import ListContainer from '@/components/ListContainer.vue';
import SharingSettingsVisibilitySelector from '@/components/sharing-settings/SharingSettingsVisibilitySelector.vue';

import useAuth from '@/composables/useAuth';
import { useFieldPresets } from '@/composables/useFieldPresets';
import {
  CROSSBEAM_DEFAULT_PRESET_TYPE,
  PARTNER_RULE,
  POPULATION_RULE,
} from '@/constants/data_shares';
import { TEMP_TEAM_DEFAULT_PRESET } from '@/constants/feature_flags';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  useFeatureFlagStore,
  usePartnersStore,
} from '@/stores';
import type { DataSharePreset } from '@/types/data_share_presets';
import type { Partner } from '@/types/partners';

type Props = {
  feedId: number;
  sourceId?: number;
};
const { feedId, sourceId } = defineProps<Props>();

const emit = defineEmits<{
  'delete-preset-requested': [preset: { id: number; name: string }];
}>();

const { sortedPresets, isOnlyPreset, createPreset, setTeamDefault } =
  useFieldPresets({
    feedId,
    sourceId,
    eventSite: EVENT_SITES.DATA_PRESETS_TAB,
  });
const dataSharePresetsStore = useDataSharePresetsStore();
const { ready } = storeToRefs(dataSharePresetsStore);
const { isTeamDefault } = dataSharePresetsStore;
const { hasFeatureFlag } = useFeatureFlagStore();

const hasTeamDefaultFlag = computed(() =>
  hasFeatureFlag(TEMP_TEAM_DEFAULT_PRESET),
);
const dataSharesStore = useDataSharesStore();
const partnersStore = usePartnersStore();
const router = useRouter();
const { isAdminRoleType } = useAuth();

const canSetTeamDefault = computed(
  () => hasTeamDefaultFlag.value && isAdminRoleType.value,
);

// Calculate preset usage - returns partner objects for each preset
const presetUsageCounts = computed<Record<number, Partner[]>>(() => {
  const partnersByPreset: Record<number, Partner[]> = {};

  // Get all sharing rules that use presets
  const allRules = dataSharesStore.getOutgoingSharingRules({});

  // Get partners using each preset
  sortedPresets.value.forEach((preset) => {
    const partnersUsingPreset = new Set<number>();

    // Check population-level rules (default for all partners without overrides)
    const populationRules = allRules.filter(
      (rule) =>
        rule.rule_type === POPULATION_RULE &&
        rule.data_share_presets_id === preset.id,
    );

    populationRules.forEach((popRule) => {
      // Find partner-specific overrides for this population
      const partnerOverrides = allRules.filter(
        (rule) =>
          rule.rule_type === PARTNER_RULE &&
          rule.population_id === popRule.population_id &&
          rule.partner_org_id !== null,
      );

      const partnersWithOverrides = new Set(
        partnerOverrides
          .map((rule) => rule.partner_org_id)
          .filter((id): id is number => id !== null),
      );

      // Add all partners except those with overrides
      partnersStore.partnerOrgs.forEach((partner) => {
        if (!partnersWithOverrides.has(partner.id)) {
          partnersUsingPreset.add(partner.id);
        }
      });
    });

    // Check partner-specific rules that use this preset
    const partnerRules = allRules.filter(
      (rule) =>
        rule.rule_type === PARTNER_RULE &&
        rule.data_share_presets_id === preset.id &&
        rule.partner_org_id !== null,
    );

    partnerRules.forEach((rule) => {
      if (rule.partner_org_id !== null) {
        partnersUsingPreset.add(rule.partner_org_id);
      }
    });

    // Convert partner IDs to partner objects
    partnersByPreset[preset.id] = Array.from(partnersUsingPreset)
      .map((partnerId) => {
        const partner = partnersStore.partnerOrgs.find(
          (p) => p.id === partnerId,
        );
        return partner ?? null;
      })
      .filter((p): p is Partner => p !== null);
  });

  return partnersByPreset;
});

function handleCreatePreset() {
  const { id, tab, source_id } = router.currentRoute.value.params;
  router.push({
    name: 'field-preset-new',
    params: { id, tab, source_id },
  });
}

function handleEditPreset(presetId: number) {
  const { id, tab, source_id } = router.currentRoute.value.params;
  router.push({
    name: 'field-preset-edit',
    params: {
      id,
      tab,
      source_id,
      presetId: presetId.toString(),
    },
  });
}

function getPartnersForPreset(presetId: number) {
  return presetUsageCounts.value[presetId] ?? [];
}

function hasPartnersUsingPreset(presetId: number): boolean {
  return !!getPartnersForPreset(presetId).length;
}

function getUsageCount(presetId: number): string {
  const partners = getPartnersForPreset(presetId);
  const count = partners.length;
  return count === 0 ? '-' : `${count} ${pluralize(count, 'partner')}`;
}

const openMenuPresetId = ref<number | null>(null);

function setMenuOpen(presetId: number, isOpen?: boolean) {
  openMenuPresetId.value = isOpen ? presetId : null;
}

function closeMenu() {
  openMenuPresetId.value = null;
}

async function handleDuplicate(preset: {
  id: number;
  name: string | null;
  source_fields: { source_field_id: number }[];
}) {
  const fieldIds = preset.source_fields.map((field) => field.source_field_id);
  const baseName =
    preset.name === null
      ? dataSharePresetsStore.crossbeamPresetLabel
      : preset.name;
  const duplicateName = `${baseName} Copy`;

  const isCreated = await createPreset(duplicateName, fieldIds);
  if (isCreated) closeMenu();
}

async function handleSetTeamDefault(preset: DataSharePreset) {
  const isChanged = await setTeamDefault(preset, 'kebab');
  if (isChanged) closeMenu();
}

/* Only an admin can move the team default, which deleting the default needs,
   and only an admin can delete the Crossbeam preset. */
function canDelete(preset: DataSharePreset) {
  if (hasTeamDefaultFlag.value && isAdminRoleType.value) return true;
  // The API refuses to delete the preset holding is_default, flag or not.
  return (
    preset.preset_type !== CROSSBEAM_DEFAULT_PRESET_TYPE && !preset.is_default
  );
}

function getDeleteBlockedReason(preset: DataSharePreset) {
  if (getPartnersForPreset(preset.id).length) {
    return 'This preset is currently used by one or more populations. Update those populations before deleting it.';
  }
  if (isOnlyPreset.value) {
    return 'You need at least one preset. Create another before deleting this one.';
  }
  return null;
}

function handleDelete(preset: { id: number; name: string | null }) {
  closeMenu();
  emit('delete-preset-requested', {
    id: preset.id,
    name: preset.name || 'Preset',
  });
}
</script>

<style scoped lang="pcss">
@reference "../../../../../css/theme.css";
.c-field-presets-tab {
  @apply flex flex-col gap-16;
}

.c-field-presets-tab .c-loading {
  @apply max-h-[50px];
}

.c-field-presets-tab__list {
  @apply rounded-16 overflow-hidden;
}

.c-field-presets-tab__header {
  @apply grid items-center px-20 py-12 w-full border-b border-neutral-border bg-white gap-16 grid-cols-[3fr_1fr_1fr_0.5fr];
}

.c-field-presets-tab__item {
  @apply grid items-center px-20 py-16 w-full bg-white gap-16 grid-cols-[3fr_1fr_1fr_0.5fr];
}
.c-field-presets-tab__item:hover {
  @apply bg-neutral-background-weak cursor-pointer;
}
.c-field-presets-tab__item--first {
  @apply rounded-tr-none rounded-tl-none;
}
.c-field-presets-tab__item--last {
  @apply rounded-b-16;
}

.c-field-presets-tab__actions-menu {
  @apply flex flex-col gap-2 p-6 w-[200px];
}

.c-field-presets-tab__action-item {
  @apply flex items-center gap-8 h-32 px-8 rounded-6 cursor-pointer transition-colors;
  @apply text-neutral-text-strong bg-transparent border-none text-left w-full text-[14px] whitespace-nowrap;
}

.c-field-presets-tab__action-item:hover {
  @apply bg-neutral-background-weak;
}

.c-field-presets-tab__action-item:focus {
  @apply outline-none;
}
</style>
